import React, { useState, useEffect } from "react";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { XMLParser } from "fast-xml-parser";
import { postSoapRequest } from "../../services/TypeAnnonce_service";
import { getNSTypeResult } from "../../models/typeAnnonce_model";
import LoaderAnnonce from "../LoaderAnnonce";
import type { AxiosResponse } from "axios";
import { TabStypeItem } from "../../models/typeAnnonce";
import "../../styles/redaction.css";
import useAppContext from "../../hooks/useAppContext";
import { AppContextType } from "../../context/AppContext";

interface TypeAnnonceProps {
  onClick: (selectedType: TabStypeItem) => void;
  onCheckboxChange: (index: number) => void;
  setIsSubmitted: (isSubmitted: boolean) => void;
  checkedIndex: number;
}

const TypeAnnonce: React.FC<TypeAnnonceProps> = ({
  onClick,
  onCheckboxChange,
  checkedIndex,
  setIsSubmitted,
}) => {
  const { setAnnouncementTypeList, clearContext, setLastTab } =
    useAppContext() as AppContextType;

  const [responseJson, setResponseJson] = useState<any>(null);
  const [tabStypeArray, setTabStypeArray] = useState<TabStypeItem[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>("");
  const [noData, setNoData] = useState<boolean>(false);

  // Déclaration de l'annonce personnalisée
  const customArrivalAnnonce: TabStypeItem = {
    nType: 2,
    nSType: 22,
    sSType: "Transfert siège hors département (Annonce d'arrivée)",
  };

  useEffect(() => {
    // démarre la requête à la montée du composant
    callSoapApi();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setIsSubmitted(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCheckboxChange = (index: number) => {
    onCheckboxChange(index);
  };

  // Appel API encapsulé (peut être réutilisé pour retry)
  const callSoapApi = async () => {
    setLoading(true);
    setError("");
    setNoData(false);
    try {
      const responseXml: AxiosResponse<any> = await postSoapRequest();

      const parser = new XMLParser();
      const jsonResponse = parser.parse(responseXml.data);
      setResponseJson(jsonResponse);

      const responseEnvelope =
        jsonResponse?.["SOAP-ENV:Envelope"]?.["SOAP-ENV:Body"]?.["Get_nSTypeResult"];

      const rawTabStype = responseEnvelope?.tabStype;
      const normalizedTabStype: TabStypeItem[] = Array.isArray(rawTabStype)
        ? rawTabStype
        : rawTabStype
          ? [rawTabStype]
          : [];

      // mettre dans le contexte et le state local (toujours un tableau)
      setAnnouncementTypeList(normalizedTabStype);
      setTabStypeArray(normalizedTabStype);

      // ── TEMPORAIRE : À supprimer après vérification ──────────────────
      console.log("═══════════════════════════════════════════════════");
      console.log("📋 LISTE COMPLÈTE DES TYPES D'ANNONCES DEPUIS SOAP");
      console.log("═══════════════════════════════════════════════════");

      // Tableau lisible dans la console
      console.table(
        normalizedTabStype.map((t) => ({
          nType: t.nType,
          nSType: t.nSType,
          sSType: t.sSType,
          groupe: t.nType === 1 ? "CONSTITUTION"
            : t.nType === 2 ? "MODIFICATION"
              : t.nType === 3 ? "CESSATION"
                : t.nType === 4 ? "DIVERS"
                  : "INCONNU",
        }))
      );

      // Copier-collable pour les URLs
      console.log("\n📎 FORMAT URL (copier-coller) :");
      normalizedTabStype.forEach((t) => {
        const url = `?mode=assiste&nType=${t.nType}&nSType=${t.nSType}&type=${encodeURIComponent(t.sSType)}&step=saisie`;
        console.log(`[nType=${t.nType} | nSType=${t.nSType}] ${t.sSType}`);
        console.log(`   → ${url}`);
      });
      console.log("═══════════════════════════════════════════════════");
      // ── FIN TEMPORAIRE ───────────────────────────────────────────────

      // si aucune donnée reçue -> message informatif
      if (normalizedTabStype.length === 0) {
        setNoData(true);
        setError("Aucune donnée retournée par l'API SOAP.");
        toast.info("Aucune donnée reçue. Cliquez sur Réessayer ou contactez l'API.");
      } else {
        setNoData(false);
        setError("");
      }

      // comparaison optionnelle avec modèle
      if (responseEnvelope) {
        if (
          responseEnvelope.sMessage === getNSTypeResult.sMessage &&
          JSON.stringify(normalizedTabStype) ===
          JSON.stringify(getNSTypeResult.tabStype)
        ) {
          toast.success("Appel API réussi et la réponse correspond au modèle.");
        }
      }

      setLoading(false);
    } catch (err) {
      console.error("Erreur callSoapApi:", err);
      setLoading(false);
      setNoData(true);
      if (err instanceof Error) {
        setError(err.message);
        toast.error("Erreur lors de l'appel API SOAP: " + err.message);
      } else {
        setError("Erreur inconnue lors de l'appel API SOAP.");
        toast.error("Erreur inconnue lors de l'appel API SOAP.");
      }
    }
  };

  // Défensif : accepte undefined / objet / tableau et renvoie toujours 4 groupes
  const divideIntoGroups = (items?: TabStypeItem[] | any) => {
    if (!items || !Array.isArray(items)) {
      return [[], [], [], []] as TabStypeItem[][];
    }
    const groups: TabStypeItem[][] = [[], [], [], []];
    items.forEach((item: TabStypeItem) => {
      const nType = item?.nType;
      if (typeof nType === "number" && nType >= 1 && nType <= 4) {
        groups[nType - 1].push(item);
      }
    });
    return groups;
  };

  // Fonction pour modifier les libellés et ajouter l'annonce d'arrivée
  const processGroupsWithCustomAnnonce = (groups: TabStypeItem[][]) => {
    if (!Array.isArray(groups) || groups.length < 4) {
      groups = [groups[0] || [], groups[1] || [], groups[2] || [], groups[3] || []];
    }

    groups[1] = groups[1].map((item: TabStypeItem) => {
      if (item.sSType === "Transfert de siège hors département") {
        return {
          ...item,
          sSType: "Transfert siège hors département (Annonce de départ)",
        };
      }
      return item;
    });

    const departIndex = groups[1].findIndex((item: TabStypeItem) =>
      item.sSType === "Transfert siège hors département (Annonce de départ)"
    );

    if (departIndex !== -1) {
      const arrivalExists = groups[1].some(
        (item: TabStypeItem) => item.sSType === customArrivalAnnonce.sSType
      );
      if (!arrivalExists) {
        groups[1].splice(departIndex + 1, 0, customArrivalAnnonce);
      }
    } else {
      if (!groups[1].some((item: TabStypeItem) => item.sSType === customArrivalAnnonce.sSType)) {
        groups[1].push(customArrivalAnnonce);
      }
    }

    return groups;
  };

  const handleTypeClick = (item: TabStypeItem) => {
    setLastTab("");
    clearContext();
    onClick(item);
  };

  // Rendu loading
  if (loading) {
    return <LoaderAnnonce />;
  }

  // Si aucune donnée ou erreur — affiche un message avec bouton Réessayer
  if (noData && tabStypeArray.length === 0) {
    return (
      <div style={{ padding: 24 }}>
        <h6 className="mx-3 bg-orange mt-2">CHOIX DU TYPE D'ANNONCE</h6>
        <div className="alert alert-warning" role="alert">
          <strong>Pas de données</strong>
          <p style={{ margin: "8px 0" }}>
            {"Aucune donnée n'a été reçue depuis l'API SOAP. Veuillez cliquer pour réessayer."}
            {/* {error || "Aucune donnée n'a été reçue depuis l'API SOAP."} */}
          </p>
          <div style={{ display: "flex", gap: 8, alignItems: "center", justifyContent: "center" }}>
            <button
              className="btn btn-primary"
              onClick={() => {
                callSoapApi();
              }}
            >
              Réessayer
            </button>
          </div>
        </div>
        <ToastContainer />
      </div>
    );
  }

  // Construction des groupes à partir du tableau normalisé tabStypeArray
  let groups = divideIntoGroups(tabStypeArray);
  groups = processGroupsWithCustomAnnonce(groups);

  // Normalisation + filtrage du groupe 3
  const normalize = (s?: string) =>
    (s || "").replace(/\u00A0/g, " ").trim().toLowerCase();

  const stripAccents = (s: string) => s.normalize("NFD").replace(/[\u0300-\u036f]/g, "");

  const excludedNamesNoAccent = new Set(["marches publics", "adjudications"]);

  const filteredGroup3 = groups[3].filter((item: TabStypeItem) => {
    const sNorm = normalize(item.sSType);
    const sNoAccent = stripAccents(sNorm);

    if (excludedNamesNoAccent.has(sNoAccent)) return false;

    if (sNorm === "annonce additive" || sNorm === "annonce rectificative") return false;
    if (checkedIndex === 1) {
      if (sNorm === "avis administratifs") return false;
      if (sNorm === "autre (rédaction libre)") return false;
    }
    return true;
  });

  return (
    <>
      <h6 className="mx-3 bg-orange mt-2">CHOIX DU MODE DE REDACTION</h6>

      <div className="mx-2 row mb-3 pt-2" >
        {/* Modifier ici pour rendre responsive en une colonne en mobile */}
        <div className="col-12 col-lg-6 mb-3 mb-lg-0" >
          <div
            className={`mode ${checkedIndex === 1 ? "active" : ""}`}
            onClick={() => handleCheckboxChange(1)}
            style= {{borderRadius: "10px"}}
          >
            <div className="ml-2 mt-2 mb-2">
              <div className="header-content">
                <input
                  className="flex pr-5 mb-2"
                  type="checkbox"
                  checked={checkedIndex === 1}
                  onChange={(e) => e.stopPropagation()}
                />
                <h6 className="mr-4">MODE REDACTION ASSISTÉE</h6>
              </div>
              <p className={`paragraph ${checkedIndex === 1 ? "color-wh" : ""}`}>
                Formulaire à remplir selon le type d'annonce sélectionné.
              </p>
            </div>
          </div>
        </div>

        <div className="col-12 col-lg-6">
          <div
            className={`redaction ${checkedIndex === 0 ? "active" : ""}`}
            onClick={() => handleCheckboxChange(0)}
            style= {{borderRadius: "10px"}}
          >
            <div className="ml-2 mt-2 mr-2">
              <div className="header-content">
                <div style={{ width: "30px" }}>
                  <input
                    className="flex pr-5"
                    type="checkbox"
                    checked={checkedIndex === 0}
                    onChange={(e) => e.stopPropagation()}
                  />
                </div>
                <h6>MODE REDACTION LIBRE</h6>
              </div>
              <p className={`paragraph ${checkedIndex === 0 ? "color-wh" : ""}`}>
                Saisie et mise en forme de l'annonce en mode libre.
              </p>
            </div>
          </div>
        </div>
      </div>

      <h6 className="mx-3 bg-orange mt-2">CHOIX DU TYPE D'ANNONCE</h6>

      {/* Modifier ici pour rendre les sections en une colonne en mobile */}
      <div className="mx-2 row mb-3">
        {/* Section 1: CONSTITUTION DE SOCIETE */}
        <div className="col-12 col-md-6 col-lg-6 mb-4">
          <h6 className="bg-societe">CONSTITUTION DE SOCIETE</h6>
          <ul style={{ padding: 0 }}>
            {groups[0].map((item: TabStypeItem) => {
              if (item.sSType === "Création autre hors forfait") return null;
              return (
                <li
                  className="bg-gris py-2 border annonce-item"
                  key={`${item.nType}-${item.nSType}-${item.sSType}`}
                  onClick={() => handleTypeClick(item)}
                >
                  <span className="mx-2 annonce-type">{item.sSType}</span>
                </li>
              );
            })}
          </ul>
        </div>

        {/* Section 2: MODIFICATION DE SOCIETE */}
        <div className="col-12 col-md-6 col-lg-6 mb-4">
          <h6 className="bg-societe">MODIFICATION DE SOCIETE</h6>
          <ul style={{ padding: 0 }}>
            {groups[1].map((item: TabStypeItem) => {
              if (item.sSType === "Fusion" && checkedIndex === 1) return null;
              if (item.sSType === "Modification Autre") return null;
              if (checkedIndex === 1 && item.sSType === "Transformation Autre")
                return null;
              return (
                <li
                  className="bg-gris py-2 border annonce-item"
                  key={`${item.nType}-${item.nSType}-${item.sSType}`}
                  onClick={() => handleTypeClick(item)}
                >
                  <span className="mx-2 annonce-type">{item.sSType}</span>
                </li>
              );
            })}
          </ul>
        </div>

        {/* Section 3: CESSATION D'ACTIVITÉ */}
        <div className="col-12 col-md-6 col-lg-6 mb-4">
          <h6 className="bg-societe">CESSATION D'ACTIVITÉ</h6>
          <ul style={{ padding: 0 }}>
            {groups[2].map((item: TabStypeItem) => (
              <li
                className="bg-gris py-2 border annonce-item"
                key={`${item.nType}-${item.nSType}-${item.sSType}`}
                onClick={() => handleTypeClick(item)}
              >
                <span className="mx-2 annonce-type">{item.sSType}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Section 4: DIVERS AUTRES */}
        {filteredGroup3.length > 0 && (
          <div className="col-12 col-md-6 col-lg-6 mb-4">
            <h6 className="bg-societe">DIVERS AUTRES</h6>
            <ul style={{ padding: 0 }}>
              {filteredGroup3.map((item: TabStypeItem) => (
                <li
                  className="bg-gris py-2 border annonce-item"
                  key={`${item.nType}-${item.nSType}-${item.sSType}`}
                  onClick={() => handleTypeClick(item)}
                >
                  <span className="mx-2 annonce-type">{item.sSType}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
      <ToastContainer />
    </>
  );
};

export default TypeAnnonce;